import { useEffect } from 'react';
import { useBreadcrumbState } from 'context/BreadcrumbContext';

/**
 * useBreadcrumb — API cho page tùy biến breadcrumb hiển thị ở header chung.
 *
 * @param {Array<{label: string, to?: string, icon?: React.ReactNode, active?: boolean}>} items
 *        Mảng breadcrumb. Item cuối (hoặc item có `active: true`) sẽ là item
 *        đang đứng, không phải link. Component luôn tự prepend "Home".
 * @param {{ showSearch?: boolean }} [options]
 *        showSearch (mặc định true): bật/tắt thanh search ở header cho page này.
 *
 * @example
 *   useBreadcrumb([
 *     { label: 'CRM', to: '/dashboard/crm' },
 *     { label: 'Payment Request', to: '/crm/payment-request' },
 *     { label: pr?.code || 'Detail' },           // item cuối = active
 *   ], { showSearch: false });
 */
const useBreadcrumb = (items, options = {}) => {
  const { showSearch = true } = options;
  const { setItems, setSearchVisible } = useBreadcrumbState();

  // Ổn định dependency: chỉ chạy lại khi nội dung items thực sự đổi.
  const itemsKey = items ? JSON.stringify(items) : null;

  useEffect(() => {
    setItems(items && items.length ? items : null);
    setSearchVisible(showSearch);

    // Rời page: reset về auto-fallback + hiện lại search.
    return () => {
      setItems(null);
      setSearchVisible(true);
    };
    // items is a fresh array each render; itemsKey (JSON) is the content-equality dep, so depending on items would reset every render
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemsKey, showSearch]);
};

export default useBreadcrumb;
